import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@monksflow/monks-ui';
import {
  deliveryJobsQueryOptions,
  useCreateJob,
  knownPlatforms,
  usePlatformSchemas,
  useDeleteJob,
  useDownloadJobSource,
  useDownloadMediaPlanTemplate,
  serverReasonOf,
  JOB_STATUS_LABELS,
  type ConfirmedAsset,
  type DeliveryJob,
} from '@/entities/media-campaign';
import {
  DeliveryJobEmptyState,
  DeliveryJobGrid,
  DeliveryJobToolbar,
  EMPTY_JOB_FILTERS,
  filterJobs,
  hasActiveJobFilters,
  type JobFilterState,
} from '@/features/delivery-job-grid';
import { NewJobDialog, type NewJobSubmit } from '@/features/delivery-job-create';
import type { ProjectDetailOutletContext } from '@/pages/ProjectDetail/projectDetail.types';
import {
  LeavesTogether,
  SettledContent,
  SETTLED_EXIT_SECONDS,
  SETTLED_STATE,
} from '@/shared/ui/animations';
import { useDelayedFlag } from '@/shared/hooks';
import { ROUTES } from '@/shared/config/routes';

import { notificationService } from '@/lib/services/notification.service';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

/** Statuses that mean something of this job's is, or was, on an ad account. */
// eslint-disable-next-line i18next/no-literal-string -- wire status enum values, not copy
const PUBLISHED_STATUSES = new Set(['publishing', 'published', 'partially_published']);

function hasGoneOut(job: DeliveryJob): boolean {
  return PUBLISHED_STATUSES.has(job.status);
}

/** Enough to recognise which files, without a snackbar nobody reads to the end of. */
const NAMES_SHOWN = 3;
/** Fewer, because each of these is a whole sentence rather than a filename. */
const REASONS_SHOWN = 2;

function shortList(
  items: string[],
  shown: number,
  separator: string,
  t: TFunction<'projects'>,
): string {
  const head = items.slice(0, shown).join(separator);
  const rest = items.length - shown;
  // A space rather than the separator: "c.jpg, and 2 more" reads as a list item missing.
  return rest > 0 ? `${head} ${t('projectDetail.delivery.andMoreFiles', { count: rest })}` : head;
}

/**
 * What to say about the creatives that did not land.
 *
 * Every wording names every file that failed, whatever the cause, because the names are
 * what tell the operator which rows to go to. Only the advice differs, and it is chosen by
 * the most useful thing we know:
 *
 * - the server explained the refusal in a sentence of its own, which is the only thing that
 *   says *why*; re-uploading the same file would be refused again for the same reason
 * - a call was refused outright, so retrying a row cannot help until whatever refused it is
 *   fixed. It says which creatives, not "the creatives": one chunk of a large selection can
 *   be refused while the others store
 * - nothing more is known, so the row is the place to retry
 */
function creativeFailureMessage(failed: ConfirmedAsset[], t: TFunction<'projects'>): string {
  // One entry per file, carrying that file's own explanation when the server gave one. A
  // single list rather than a list of names plus a list of reasons, which named the
  // explained files twice and the rest not at all. The server's full stop comes off so the
  // entry sits in a comma list like any other.
  const entries = failed.map((creative) => {
    const reason = serverReasonOf(creative)?.replace(/\.$/, '');
    return {
      isExplained: Boolean(reason),
      text: reason ? `${creative.fileName} — ${reason}` : creative.fileName,
    };
  });
  const anyExplained = entries.some((entry) => entry.isExplained);

  // Explained entries first, because the list gets truncated and the reason is the one part
  // worth keeping: the refused wording drops the retry advice on the strength of having it,
  // so a reason cut from the tail leaves a message that says neither why nor what to do.
  const ordered = [...entries].sort((a, b) => Number(b.isExplained) - Number(a.isExplained));

  const details = shortList(
    ordered.map((entry) => entry.text),
    anyExplained ? REASONS_SHOWN : NAMES_SHOWN,
    ', ',
    t,
  );

  if (anyExplained) {
    return t('projectDetail.delivery.someCreativesWereRefused', {
      count: failed.length,
      details,
    });
  }

  if (failed.some((creative) => creative.reason === 'batchFailed')) {
    return t('projectDetail.delivery.couldNotUploadTheCreatives', {
      count: failed.length,
      fileNames: details,
    });
  }

  return t('projectDetail.delivery.someCreativesDidNotUpload', {
    count: failed.length,
    fileNames: details,
  });
}

/**
 * Project Detail → Delivery.
 *
 * The Jobs list: plans on their way out. Everything about one Job lives on its
 * own review page.
 */
export function Delivery() {
  const { t } = useTranslation('projects');
  const { project } = useOutletContext<ProjectDetailOutletContext>();
  const navigate = useNavigate();
  const projectId = project.platformProjectId;

  const [jobFilters, setJobFilters] = useState<JobFilterState>(EMPTY_JOB_FILTERS);
  const [isNewJobOpen, setIsNewJobOpen] = useState(false);

  const {
    data: jobs,
    isLoading: isLoadingJobs,
    isError: hasJobsLoadFailed,
    refetch: refetchJobs,
  } = useQuery(deliveryJobsQueryOptions(projectId));

  const createJob = useCreateJob(projectId);
  const [uploadProgress, setUploadProgress] = useState<number | undefined>(undefined);

  // Closing or reloading the tab mid-upload cancels it and leaves a job with no plan. Only
  // while the file is still going up: once a zip is queued, the worker no longer needs the tab.
  useEffect(() => {
    if (!createJob.isPending) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Still what Chrome and Edge below 119 read to raise the dialog.
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- see above
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [createJob.isPending]);
  const downloadTemplate = useDownloadMediaPlanTemplate();
  const deleteJob = useDeleteJob(projectId);
  const downloadSource = useDownloadJobSource();
  /**
   * Held rather than deleted on the spot. A job that has published owns live objects on
   * an ad account, and deleting it loses track of them rather than undoing them — which
   * a menu item alone does not say.
   */
  const [jobToDelete, setJobToDelete] = useState<DeliveryJob | null>(null);
  // A new Job binds every platform we can parse, and that list comes from the
  // schema endpoint — so this page has to have loaded it before anyone can submit,
  // or the job is created with no platforms at all.
  const schemas = usePlatformSchemas();

  const allJobs = useMemo(() => jobs ?? [], [jobs]);
  const visibleJobs = useMemo(() => filterJobs(allJobs, jobFilters), [allJobs, jobFilters]);
  // Only the platforms these jobs actually touch, so the filter can never offer
  // an option that returns nothing.
  const jobPlatforms = useMemo(
    () => [...new Set(allJobs.flatMap((job) => job.platforms))],
    [allJobs],
  );

  const openJob = (job: DeliveryJob) =>
    void navigate(ROUTES.PROJECT_DELIVERY_JOB(projectId, job.id));

  const handleCreateJob = (input: NewJobSubmit) => {
    const platforms = knownPlatforms();
    if (!schemas.isReady || platforms.length === 0) {
      // Nothing to bind means nothing could be parsed, and the server rejects a job
      // with no platforms. Say so rather than sending a request that cannot succeed.
      notificationService.showError(
        schemas.isError
          ? t('projectDetail.delivery.couldNotLoadThePlatformList')
          : t('projectDetail.delivery.stillLoadingThePlatformList'),
      );
      return;
    }

    createJob.mutate(
      // Every platform we can parse, because only the sheet knows which ones this
      // plan covers. The upload drops the bindings it finds no rows for, so a
      // Meta-only plan does not leave an empty Google tab behind.
      { ...input, platforms, onUploadProgress: setUploadProgress },
      {
        onSettled: () => setUploadProgress(undefined),
        onSuccess: ({ job, creatives, ingested }) => {
          setIsNewJobOpen(false);
          void navigate(ROUTES.PROJECT_DELIVERY_JOB(projectId, job.id));

          // Named after the navigation, not instead of it: the plan parsed, so the job is
          // real and the reviewer belongs on it. A creative that did not land is fixed
          // from the row that wanted it, which is the screen they just arrived at.
          //
          // The uploads outlive this screen, so the toast is raised from the promise rather
          // than from what the mutation returned. It survives the unmount because the
          // notification service is a module-level queue and not this tree's state.
          void creatives.then((outcomes) => {
            const failed = outcomes.filter((creative) => creative.status === 'failed');
            if (failed.length > 0) {
              notificationService.showError(creativeFailureMessage(failed, t));
            }
          });

          // A zip is read on a worker after this, so whether it became a plan arrives later,
          // possibly once the reviewer has moved on to something else.
          void ingested?.then((read) => {
            if (read.isReadingSource) return;
            if (read.sourceError) {
              notificationService.showError(read.sourceError, { persist: true });
              return;
            }
            notificationService.showSuccess(
              t('projectDetail.delivery.planIsReadyToReview', { title: read.title }),
            );
          });
        },
        onError: (error) =>
          notificationService.showError(
            error instanceof Error
              ? error.message
              : t('projectDetail.delivery.couldNotReadThatPlan'),
          ),
      },
    );
  };

  // Nothing announces loading until it has lasted long enough to be worth it.
  const showLoadingIndicator = useDelayedFlag(isLoadingJobs && visibleJobs.length === 0);
  // The truth about loading, not the indicator's timing: reading the delayed
  // flag here would call an unanswered query empty for its first quarter second.
  const isLoadingEmpty = isLoadingJobs && visibleJobs.length === 0;
  // Nothing to search or filter when the answer is that there is nothing, so
  // the toolbar leaves with the table. It stays for a filtered miss, which is
  // the one empty result the toolbar itself can undo.
  const hasActiveFilters = hasActiveJobFilters(jobFilters);
  const downloadJobTemplate = () =>
    downloadTemplate.mutate(undefined, {
      onError: () =>
        notificationService.showError(t('projectDetail.delivery.couldNotDownloadTheTemplate')),
    });
  const showToolbar = visibleJobs.length > 0 || showLoadingIndicator || hasActiveFilters;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4" data-testid="project-delivery-section">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-card">
        <LeavesTogether present={showToolbar} exitSeconds={SETTLED_EXIT_SECONDS}>
          <DeliveryJobToolbar
            pending={showLoadingIndicator}
            filters={jobFilters}
            platforms={jobPlatforms}
            onFiltersChange={setJobFilters}
            onNewJob={() => setIsNewJobOpen(true)}
            onDownloadTemplate={downloadJobTemplate}
            isDownloadingTemplate={downloadTemplate.isPending}
          />
        </LeavesTogether>

        <div className="flex min-h-0 flex-1 flex-col overflow-auto px-section pb-section">
          <SettledContent
            state={
              isLoadingEmpty
                ? SETTLED_STATE.loading
                : visibleJobs.length > 0
                  ? SETTLED_STATE.rows
                  : SETTLED_STATE.empty
            }
            className="min-h-0 flex-1"
            contentClassName="flex min-h-0 flex-col"
          >
            {isLoadingEmpty && !showLoadingIndicator ? null : (
              <DeliveryJobGrid
                jobs={visibleJobs}
                isLoading={showLoadingIndicator}
                emptyState={
                  <DeliveryJobEmptyState
                    hasActiveFilters={hasActiveFilters}
                    hasLoadFailed={hasJobsLoadFailed}
                    onNewJob={() => setIsNewJobOpen(true)}
                    onClearFilters={() => setJobFilters(EMPTY_JOB_FILTERS)}
                    onRetry={() => void refetchJobs()}
                    onDownloadTemplate={downloadJobTemplate}
                    isDownloadingTemplate={downloadTemplate.isPending}
                  />
                }
                onOpenJob={openJob}
                onDeleteJob={setJobToDelete}
                onDownloadSource={(job) =>
                  downloadSource.mutate(job, {
                    onError: () =>
                      notificationService.showError(
                        t('projectDetail.delivery.couldNotDownloadThatFile'),
                      ),
                  })
                }
              />
            )}
          </SettledContent>
        </div>
      </div>

      {/* Mounted only while open so each new job starts on a clean form: the dialog
          deliberately keeps the title and file through a failed submit, and the
          success path closes it without going through the cancel handler. */}
      {isNewJobOpen && (
        <NewJobDialog
          open={isNewJobOpen}
          isSubmitting={createJob.isPending}
          uploadProgress={uploadProgress}
          onSubmit={handleCreateJob}
          onClose={() => setIsNewJobOpen(false)}
        />
      )}

      <AlertDialog
        open={jobToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setJobToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('projectDetail.delivery.deleteJobTitle', { title: jobToDelete?.title ?? '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {/* The distinction worth spelling out: a published job owns objects on an
                  ad account, and this removes the plan rather than the campaigns. */}
              {jobToDelete && hasGoneOut(jobToDelete)
                ? t('projectDetail.delivery.deletePublishedDetail', {
                    status: JOB_STATUS_LABELS[jobToDelete.status].toLowerCase(),
                  })
                : t('projectDetail.delivery.deleteDetail')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('projectDetail.delivery.keepIt')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleteJob.isPending}
              onClick={() => {
                if (!jobToDelete) return;
                deleteJob.mutate(jobToDelete.id, {
                  onSuccess: () => setJobToDelete(null),
                  onError: (error) =>
                    notificationService.showError(
                      error instanceof Error
                        ? error.message
                        : t('projectDetail.delivery.couldNotDeleteThatJob'),
                    ),
                });
              }}
            >
              {deleteJob.isPending
                ? t('projectDetail.delivery.deleting')
                : t('projectDetail.delivery.deleteJob')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
